import React from 'react';
import { Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import SearchableSelect from '../../common/SearchableSelect';
import { Account, Segment } from '../../../interfaces/accounting.interface';
import { departmentLabel } from '../../costcentre/DepartmentPicker'; // B4-H
import { DepartmentOption } from '../../../interfaces/costCentre.interface'; // B4-H
import { ProjectOption } from '../../../interfaces/project'; // P1-A project
import { projectLabel } from '../../project/ProjectSelect'; // P1-A project
import { GridRow, applyPaste, parsePaste } from './budgetGridUtils';

interface Props {
    rows: GridRow[];
    months: { year: number; month: number; label: string }[];
    accounts: Account[];
    segments: Segment[];
    departments?: DepartmentOption[]; // B4-H
    projects?: ProjectOption[]; // P1-A project
    readOnly: boolean;
    onChange: (rows: GridRow[]) => void;
}

const BudgetGrid: React.FC<Props> = ({ rows, months, accounts, segments, departments = [], projects = [], readOnly, onChange }) => {
    const setCell = (ri: number, ci: number, v: string) =>
        onChange(rows.map((r, i) => i === ri ? { ...r, amounts: r.amounts.map((a, j) => j === ci ? v : a) } : r));
    const setRow = (ri: number, patch: Partial<GridRow>) => onChange(rows.map((r, i) => i === ri ? { ...r, ...patch } : r));
    const total = (r: GridRow) => r.amounts.reduce((s, a) => s + (Number(a.replace(/,/g, '')) || 0), 0);

    const onPaste = (ri: number, ci: number) => (e: React.ClipboardEvent) => {
        const text = e.clipboardData.getData('text');
        if (!text.includes('\t') && !text.includes('\n')) return; // plain single value: let the input handle it
        e.preventDefault();
        onChange(applyPaste(rows, ri, ci, parsePaste(text), months.length));
    };

    return (
        <Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                <Table data-help-section="accounting.budgetGrid.table" size="small" stickyHeader sx={{ minWidth: 560 + (departments.length ? 170 : 0) + (projects.length ? 170 : 0) + months.length * 95 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.budgetGrid.col.account" sx={{ minWidth: 240 }}>Account</TableCell>
                            <TableCell data-help="accounting.budgetGrid.col.segment" sx={{ minWidth: 150 }}>Segment</TableCell>
                            {departments.length > 0 && <TableCell data-help="accounting.budgetGrid.col.department" sx={{ minWidth: 170 }}>Department</TableCell>}{/* B4-H */}
                            {projects.length > 0 && <TableCell data-help="accounting.budgetGrid.col.project" sx={{ minWidth: 170 }}>Project</TableCell>}{/* P1-A project */}
                            {months.map(m => <TableCell data-help="accounting.budgetGrid.col.month" key={`${m.year}-${m.month}`} align="right">{m.label}</TableCell>)}
                            <TableCell data-help="accounting.budgetGrid.col.total" align="right">Total</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r, ri) => (
                            <TableRow key={r.key} hover>
                                <TableCell>
                                    <SearchableSelect data-help="accounting.budgetGrid.account" options={accounts} value={r.accountId} disabled={readOnly} size="small"
                                        getOptionLabel={a => `${a.code} ${a.name}`} onChange={id => setRow(ri, { accountId: id })} placeholder="Account" />
                                </TableCell>
                                <TableCell>
                                    <SearchableSelect data-help="accounting.budgetGrid.segment" options={segments} value={r.segmentId} disabled={readOnly} size="small"
                                        getOptionLabel={s => `${s.code} ${s.name}`} onChange={id => setRow(ri, { segmentId: id })} placeholder="All" />
                                </TableCell>
                                {departments.length > 0 && (
                                    <TableCell>{/* B4-H */}
                                        <SearchableSelect data-help="accounting.budgetGrid.department" options={departments} value={r.departmentId ?? ''} disabled={readOnly} size="small"
                                            getOptionLabel={departmentLabel} onChange={id => setRow(ri, { departmentId: id })} placeholder="All" />
                                    </TableCell>
                                )}
                                {projects.length > 0 && (
                                    <TableCell>{/* P1-A project */}
                                        <SearchableSelect data-help="accounting.budgetGrid.project" options={projects} value={r.projectId ?? ''} disabled={readOnly} size="small"
                                            getOptionLabel={projectLabel} onChange={id => setRow(ri, { projectId: id })} placeholder="None" />
                                    </TableCell>
                                )}
                                {months.map((m, ci) => (
                                    <TableCell key={ci} sx={{ p: 0.5 }}>
                                        <TextField data-help="accounting.budgetGrid.amount" size="small" value={r.amounts[ci]} disabled={readOnly}
                                            onChange={e => setCell(ri, ci, e.target.value)} onPaste={onPaste(ri, ci)}
                                            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', minWidth: 70 }, 'aria-label': `${m.label} row ${ri + 1}` }} />
                                    </TableCell>
                                ))}
                                <TableCell align="right">{total(r).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                <TableCell>
                                    {!readOnly && (
                                        <Tooltip title="Remove row"><IconButton data-help="accounting.budgetGrid.action.removeRow" size="small" onClick={() => onChange(rows.filter((_, i) => i !== ri))}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && (
                            <TableRow><TableCell colSpan={months.length + 4}><Typography variant="body2" color="text.secondary">No lines yet. Add a row, paste from Excel, or import a workbook.</Typography></TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default BudgetGrid;
